/**
 * The tenant's default models for new agents (V1.2-UX-T05). In the builder: whether this agent's speech, language
 * model and voice are the default, "Use the default" to copy it in (a change to save like any other), and, for an
 * admin, "Make these the default". On the Models page: what the default is, and clearing it. A default is copied
 * into an agent when it is made, never followed: changing it changes no agent.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { data, type Schemas } from "../../api/client";
import { useApiQuery } from "../../app/Page";
import { useBff, useCan, useMe } from "../../app/session";
import { useT } from "../../i18n";
import { Button } from "../../ui/Button";
import { ErrorState } from "../../ui/states";
import type { Builder } from "./useBuilder";

type Models = Schemas["DefaultModels"];
const ROLES = ["stt", "llm", "tts"] as const;

/** The same JSON, whatever the order of its keys. */
function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value).sort().map((k) => `${JSON.stringify(k)}:${canonical((value as Record<string, unknown>)[k])}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

function useSaveDefault() {
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (models: Models | null) => data(api.PATCH("/v1/tenant", { body: { default_models: models } })),
    onSuccess: () => Promise.all([
      queries.invalidateQueries({ queryKey: ["tenant", tenant] }), queries.invalidateQueries({ queryKey: ["tenant"] }),
    ]),
  });
}

export function DefaultModelsBar({ builder }: { builder: Builder }) {
  const t = useT();
  const canChange = useCan("settings:write");
  const tenant = useApiQuery("tenant", (a) => a.GET("/v1/tenant"));
  const save = useSaveDefault();
  const current = Object.fromEntries(ROLES.map((r) => [r, builder.doc.get(["spec", r])])) as Partial<Models>;
  const complete = ROLES.every((r) => current[r] && typeof current[r] === "object");
  const defaults = tenant.data?.default_models ?? null;
  const same = defaults !== null && canonical(defaults) === canonical(current);
  if (!defaults && !(canChange && complete)) return null;
  const use = () => {
    if (!defaults) return;
    for (const r of ROLES) builder.setPath(["spec", r], structuredClone(defaults[r]));
  };
  if (same && !save.isSuccess) {
    // nothing to do: one quiet line, so the form stays short (V1.2-UX-T10)
    return <p role="group" aria-label={t("defaults.title")} className="text-xs text-[var(--color-muted)]">{t("defaults.same")}</p>;
  }
  return (
    <div role="group" aria-label={t("defaults.title")} className="space-y-2 rounded-md bg-[var(--color-surface)] p-3 text-sm">
      {defaults && <p>{t(same ? "defaults.same" : "defaults.differs")}</p>}
      <div className="flex flex-wrap gap-2">
        {defaults && !same && <Button variant="secondary" onClick={use}>{t("defaults.use")}</Button>}
        {canChange && complete && !same && (
          <Button variant="secondary" onClick={() => save.mutate(current as Models)} disabled={save.isPending}>
            {t("defaults.make")}
          </Button>
        )}
      </div>
      {save.isSuccess && same && <p role="status">{t("defaults.made")}</p>}
      {save.isError && <ErrorState error={save.error} />}
    </div>
  );
}

export function DefaultModelsSection() {
  const t = useT();
  const canChange = useCan("settings:write");
  const tenant = useApiQuery("tenant", (a) => a.GET("/v1/tenant"));
  const save = useSaveDefault();
  const defaults = tenant.data?.default_models ?? null;
  return (
    <section aria-labelledby="default-models" className="space-y-2">
      <h2 id="default-models" className="text-lg font-semibold">{t("defaults.title")}</h2>
      {defaults ? (
        <>
          <ul className="space-y-1 text-sm">
            {ROLES.map((r) => (
              <li key={r}>{t("defaults.summary", { role: t(`defaults.role.${r}`), provider: String(defaults[r].provider),
                model: defaults[r].model ? ` · ${String(defaults[r].model)}` : "" })}</li>
            ))}
          </ul>
          {canChange && (
            <Button variant="secondary" onClick={() => save.mutate(null)} disabled={save.isPending}>{t("defaults.clear")}</Button>
          )}
        </>
      ) : (
        <p className="text-sm text-[var(--color-muted)]">{t("defaults.none")}</p>
      )}
      {save.isError && <ErrorState error={save.error} />}
    </section>
  );
}
